import { useEffect, useRef } from 'react';

const FOCUS_RING =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-violet-50';
const SECONDARY_BUTTON = `inline-flex h-7 items-center rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 ${FOCUS_RING} focus-visible:ring-gray-400`;
const AI_OUTLINE_BUTTON = `inline-flex h-7 items-center rounded-md border border-violet-200 bg-white px-2.5 text-xs font-medium text-violet-700 transition-colors hover:border-violet-300 hover:bg-violet-50 ${FOCUS_RING} focus-visible:ring-violet-500`;

// Focuses the element once, when it mounts, if asked to.
function useFocusOnMount<T extends HTMLElement>(enabled: boolean) {
    const ref = useRef<T>(null);
    useEffect(() => {
        if (enabled) ref.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return ref;
}

interface AiDraftActionsProps {
    basedOn: string | null;
    onUse: () => void;
    // Set when returning from the replace confirm, so keyboard focus lands back here.
    focusOnMount?: boolean;
}

// One row: the quick reply title truncates (full title on hover) so the button never shrinks.
export function AiDraftActions({ basedOn, onUse, focusOnMount = false }: AiDraftActionsProps) {
    const draftButtonRef = useFocusOnMount<HTMLButtonElement>(focusOnMount);
    return (
        <div className="flex min-w-0 items-center gap-2">
            {basedOn && (
                <span
                    className="min-w-0 flex-1 truncate text-[11px] text-gray-600"
                    title={`Based on: ${basedOn}`}
                >
                    Based on: <span className="font-medium text-gray-700">{basedOn}</span>
                </span>
            )}
            <button
                ref={draftButtonRef}
                type="button"
                onClick={onUse}
                className={`ml-auto flex-shrink-0 ${AI_OUTLINE_BUTTON}`}
            >
                Use draft
            </button>
        </div>
    );
}

interface AiDraftReplaceConfirmProps {
    onCancel: () => void;
    onReplace: () => void;
}

// Inline, like the DM control confirm steps: a modal here would stack on the side panel.
export function AiDraftReplaceConfirm({ onCancel, onReplace }: AiDraftReplaceConfirmProps) {
    // It replaces the button that opened it, so focus moves to the safe choice.
    const keepRef = useFocusOnMount<HTMLButtonElement>(true);
    return (
        <div role="alert" className="flex flex-col gap-2">
            <span className="text-xs text-gray-700">
                Replace the message you&apos;re typing with this draft?
            </span>
            <div className="flex items-center justify-end gap-2">
                <button ref={keepRef} type="button" onClick={onCancel} className={SECONDARY_BUTTON}>
                    Keep my message
                </button>
                <button type="button" onClick={onReplace} className={AI_OUTLINE_BUTTON}>
                    Replace
                </button>
            </div>
        </div>
    );
}
